import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import serviceData from '../data/serviceData';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import WhatsAppButton from "../components/WhatsAppButton";
import AOS from 'aos';
import 'aos/dist/aos.css'; // AOS CSS for animations
import { Helmet } from 'react-helmet';

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const service = serviceData.find(item => item.id === parseInt(id));

  useEffect(() => {
    AOS.init(); // Initialize AOS for animations
  }, []);

  if (!service) return <p className="text-center mt-10">Service not found.</p>;

  // Generate SEO content based on service
  const getServiceSEO = (service) => {
    const serviceSEO = {
      1: {
        title: "Start-Ups & MSME Subsidy Services | Kavipriya Associates",
        description: "Expert guidance on Start-Ups & MSME subsidies, government schemes, financial assistance, and tax benefits. Get support for business registration and formalities.",
        keywords: "MSME Subsidy, Startup Subsidy, Government Schemes, Business Registration, Financial Assistance, Tax Benefits, MSME Support, Startup Services"
      },
      2: {
        title: "GST Services | Goods and Service Tax Filing & Compliance | Kavipriya Associates",
        description: "Professional GST registration, filing, compliance, and advisory services. Expert GST impact analysis, planning, and refund assistance for businesses.",
        keywords: "GST Services, GST Registration, GST Filing, GST Compliance, GST Advisory, GST Refund, Tax Planning, GST Consultant"
      },
      3: {
        title: "Income Tax Services | Salary, Company & TDS Filing | Kavipriya Associates",
        description: "Comprehensive Income Tax services including salary tax, corporate tax, TDS returns, tax planning, and audit support. Expert tax consultancy for individuals and businesses.",
        keywords: "Income Tax Services, TDS Filing, Salary Tax, Corporate Tax, Tax Planning, Tax Audit, Tax Consultant, Tax Returns"
      },
      4: {
        title: "Book Keeping Services | Professional Accounting & Financial Management | Kavipriya Associates",
        description: "Professional bookkeeping services including financial statements, payroll processing, and tax-compliant accounting. Expert financial management for businesses.",
        keywords: "Book Keeping, Accounting Services, Financial Statements, Payroll Processing, Tax Accounting, Financial Management, Business Accounting"
      },
      5: {
        title: "DGFT Services | Foreign Trade Management & Import Export | Kavipriya Associates",
        description: "Complete DGFT services including IEC Certificate, Certificate of Origin, Advance Authorisation, EPCG, SCOMET License, and export promotion services.",
        keywords: "DGFT Services, IEC Certificate, Import Export, Foreign Trade, Certificate of Origin, Advance Authorisation, EPCG, Export Services"
      },
      6: {
        title: "Customs Clearance & MOOWR Services | Warehouse Licensing | Kavipriya Associates",
        description: "Expert customs clearance services, MOOWR licensing, warehouse operations, bonding, and import-export clearance. Specialized in used machinery and scrap imports.",
        keywords: "Customs Clearance, MOOWR, Warehouse License, Import Export, Bonding, Used Machinery Import, Scrap Import, Customs Services"
      },
      7: {
        title: "Insurance Services | Health, Wealth & Marine Insurance | Kavipriya Associates",
        description: "Comprehensive insurance services including LIC plans, mediclaim, marine insurance, motor insurance, wealth management, and mutual fund schemes.",
        keywords: "Insurance Services, LIC Plans, Mediclaim, Marine Insurance, Motor Insurance, Wealth Management, Mutual Funds, Insurance Advisor"
      },
      8: {
        title: "Statutory Compliance Services | ESI, PF, Factory License | Kavipriya Associates",
        description: "Complete statutory compliance services including ESI, PF, Factory licensing, TNPCB clearance, Fire license, and eMudhra DSC tokens for all business requirements.",
        keywords: "Statutory Compliance, ESI, PF, Factory License, TNPCB Clearance, Fire License, eMudhra DSC, Compliance Services"
      }
    };
    return serviceSEO[service.id] || {
      title: `${service.title} | Kavipriya Associates`,
      description: `Expert ${service.title.toLowerCase()} services. Professional consultation and support for your business needs.`,
      keywords: `${service.title}, Professional Services, Business Support, Expert Consultation`
    };
  };

  const seoData = getServiceSEO(service);

  return (
    <>
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="Kavipriya Associates" />
        <link rel="canonical" href={`https://kavipriyaassociates.com/services/${service.id}`} />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://kavipriyaassociates.com/Logo.png" />
        <meta property="og:url" content={`https://kavipriyaassociates.com/services/${service.id}`} />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content="https://kavipriyaassociates.com/Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "${service.title}",
      "description": "${seoData.description}",
      "provider": {
        "@type": "Organization",
        "name": "Kavipriya Associates",
        "url": "https://kavipriyaassociates.com"
      },
      "serviceType": "${service.title}",
      "areaServed": {
        "@type": "Country",
        "name": "India"
      },
      "url": "https://kavipriyaassociates.com/services/${service.id}"
    }
    `}
        </script>
      </Helmet>

      <div className="px-6 py-20 max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-lg shadow-md hover:bg-purple-200 transition-all"
          >
            <FaArrowLeft /> Back
          </button>

          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg shadow-md hover:bg-purple-700 transition-all"
          >
            <FaHome /> Home
          </button>
        </div>

        <h2 
          className="text-3xl font-bold text-purple-800 mb-4 flex items-center gap-3" 
          data-aos="fade-up" 
          data-aos-duration="1000"
        >
          <span className="text-4xl">{service.icon}</span> {service.title}
        </h2>

        {/* Display Content with Animation for each point */}
        <div className="space-y-6" data-aos="fade-up" data-aos-duration="1500">
          <p className="text-gray-700 text-lg mb-4">{service.content[0]}</p>
          {service.content.slice(1).map((point, index) => (
            <p key={index} className="text-gray-600 text-lg">
              <span className="text-purple-600 font-semibold">•</span> {point}
            </p>
          ))}
        </div>

        <div className="mt-12" data-aos="fade-up" data-aos-duration="2000">
          <WhatsAppButton />
        </div>
      </div>
    </>
  );
};

export default ServiceDetail;
